<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

// The (i) on a product photo: ingredients, allergens and producers.
// Opens as a card over the photo on larger screens and as a bottom sheet on phones.
const props = defineProps({ product: { type: Object, required: true } })

const open = ref(false)
const root = ref(null)
const closeButton = ref(null)
const panelId = computed(() => `product-info-${props.product.id}`)

const hasInfo = computed(
  () => props.product.ingredients || props.product.allergens || props.product.producers?.length,
)

watch(open, (isOpen) => {
  if (isOpen) requestAnimationFrame(() => closeButton.value?.focus())
})

const onDocumentClick = (event) => {
  if (open.value && root.value && !root.value.contains(event.target)) open.value = false
}
const onKey = (event) => event.key === 'Escape' && (open.value = false)
onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onKey)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onKey)
})
</script>

<template>
  <div v-if="hasInfo" ref="root" class="info">
    <button
      type="button"
      class="info__button"
      :aria-expanded="open"
      :aria-controls="panelId"
      :aria-label="`Indhold og producenter for ${product.name}`"
      @click.stop="open = !open"
    >
      <span aria-hidden="true">i</span>
    </button>

    <Transition name="info">
      <div v-if="open" class="info__layer">
        <div class="info__backdrop" aria-hidden="true" @click="open = false" />
        <div :id="panelId" class="info__panel" role="dialog" :aria-label="`Om ${product.name}`">
          <div class="info__head">
            <p class="info__name">{{ product.name }}</p>
            <button ref="closeButton" type="button" class="info__close" @click="open = false">Luk</button>
          </div>
          <section v-if="product.ingredients" class="info__section">
            <h4>Ingredienser</h4>
            <p>{{ product.ingredients }}</p>
          </section>
          <section v-if="product.allergens" class="info__section">
            <h4>Allergener</h4>
            <p>{{ product.allergens }}</p>
          </section>
          <section v-if="product.producers?.length" class="info__section">
            <h4>Producenter</h4>
            <ul class="info__producers">
              <li v-for="(producer, index) in product.producers" :key="index">
                <span v-if="producer.what" class="info__what">{{ producer.what }}</span>
                <span class="info__who">{{ producer.who }}</span>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </Transition>
  </div>
</template>
